import { StreamMap } from '@m43/core';
import { SocketListener } from '@m43/sockets-mark43';
import { UseQueryResult } from '@tanstack/react-query';
import { ConversationParticipantView, ConversationView } from '@mark43/cad-api';
import { useEffect, useMemo } from 'react';
import { createStreamListener, useSocketStatus, useStream } from '../../socket/use-stream';
import { getNudgePayload } from '../nudge/helpers';
import { NudgeProcessingQueue } from '../nudge/nudge-processing-queue';
import { MessagingStore } from '../store/messaging-store';
import {
    UpdateUserIdentityNudge,
    NudgeType,
    InsertMessageNudge,
    InsertConversationNudge,
} from '../types';
import { useConversationQuery } from './resource/use-conversation';
import { useCurrentUser } from '../../current-user/current-user';

export const useMessagingStream = (
    store: MessagingStore,
    messagingBootstrapQuery: UseQueryResult<
        {
            conversations: ConversationView[];
            bookmarkedConversations: ConversationView[];
            participantIdentities: ConversationParticipantView[];
        },
        unknown
    >
) => {
    const currentUser = useCurrentUser();
    const { fetchConversation } = useConversationQuery(store);

    const nudgeProcessingQueue = useMemo(() => new NudgeProcessingQueue(), []);

    const processInsertConversationNudge = async (nudge: InsertConversationNudge) => {
        const { id: conversationId, participantIds } = nudge;
        const conversation = store.conversations?.[conversationId];
        if (
            conversation === undefined &&
            store.participantIdentities?.active !== undefined &&
            !store.conversationsToBeFetched.has(conversationId)
        ) {
            for (const identity of store.participantIdentities.active) {
                if (participantIds.includes(identity.id)) {
                    store.conversationsToBeFetched.add(conversationId);
                }
            }
        }
    };

    const processInsertMessageNudge = async (nudge: InsertMessageNudge) => {
        const { id: conversationId, senderUserId, messageId: nudgeId } = nudge;

        store.setLastReceivedMessageNudgeId(nudgeId);

        if (store.conversationsToBeFetched.has(conversationId)) {
            nudgeProcessingQueue.pause();
            try {
                await fetchConversation(conversationId, senderUserId);
            } catch {
                // This catch indicates an invalid state, (a warning error was sent to Sentry). If this was
                // due to internet disconnect, when the websocket reconnects the conversations will be reloaded
                // and a valid state will be restored.
            } finally {
                nudgeProcessingQueue.start();
            }
        } else {
            store.processInsertMessageNudge(nudge);
        }
    };

    const processUpdateIdentityNudges = async (nudges: UpdateUserIdentityNudge[]) => {
        const user = store.participantIdentities?.activeMappedByType.USER;
        if (user !== undefined && nudges.some((nudge) => nudge.user === user.id)) {
            nudgeProcessingQueue.pause();
            nudgeProcessingQueue.clear();
            await messagingBootstrapQuery.refetch();
        }
    };

    const streamListener = useMemo(
        () =>
            createStreamListener({
                streamNames: ['nudge'],
                onMessage: (message) => {
                    if (nudgeProcessingQueue.isFull) {
                        // avoid memory leak
                        nudgeProcessingQueue.pause();
                        nudgeProcessingQueue.clear();
                        messagingBootstrapQuery.refetch();
                        return;
                    }

                    const conversationNudges = getNudgePayload<InsertConversationNudge>(
                        message,
                        NudgeType.CONVERSATION
                    );

                    const messageNudges = getNudgePayload<InsertMessageNudge>(
                        message,
                        NudgeType.MESSAGE
                    );

                    const messageNudgesForCurrentUser = messageNudges.filter((nudge) =>
                        nudge.messageUserIds.includes(currentUser.userId)
                    );

                    if (messageNudgesForCurrentUser.length) {
                        store.addHistoricalConversationIdsToBeFetched(
                            messageNudgesForCurrentUser.map((nudge) => nudge.id)
                        );
                    }

                    const identityUpdateNudges = [
                        ...getNudgePayload<UpdateUserIdentityNudge>(
                            message,
                            NudgeType.DISPATCH_AREA_IDENTITY
                        ),
                        ...getNudgePayload<UpdateUserIdentityNudge>(
                            message,
                            NudgeType.STATION_IDENTITY
                        ),
                        ...getNudgePayload<UpdateUserIdentityNudge>(
                            message,
                            NudgeType.UNIT_IDENTITY
                        ),
                    ];

                    conversationNudges.forEach((nudge) => {
                        if (nudge.logT === 'INSERT') {
                            nudgeProcessingQueue.add(() => processInsertConversationNudge(nudge));
                        }
                    });

                    messageNudges.forEach((nudge) => {
                        if (nudge.logT === 'INSERT') {
                            nudgeProcessingQueue.add(() => processInsertMessageNudge(nudge));
                        }
                    });

                    if (identityUpdateNudges.length > 0) {
                        nudgeProcessingQueue.add(() =>
                            processUpdateIdentityNudges(identityUpdateNudges)
                        );
                    }
                },
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store]
    );

    useStream(streamListener);

    const socketListener: SocketListener<StreamMap> = useMemo(
        () => ({
            invokeOnInitialize: true,
            onSocketStatusChange: ({ status }) => {
                if (status === 'FATAL') {
                    nudgeProcessingQueue.pause();
                    nudgeProcessingQueue.clear();
                }
                if (status === 'CONNECTED') {
                    messagingBootstrapQuery.refetch();
                }
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useSocketStatus(socketListener);

    useEffect(() => {
        if (!messagingBootstrapQuery.isFetching && messagingBootstrapQuery.isSuccess) {
            nudgeProcessingQueue.start();
        }
    }, [messagingBootstrapQuery, messagingBootstrapQuery.isFetching, nudgeProcessingQueue]);
};
